import React from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSEO } from "@/hooks/useSEO";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Methodology = () => {
  useSEO({
    title: 'Statistical Methodology - Female Partner Calculator | How It Works',
    description: 'Learn about the statistical methodology behind the Female Partner Calculator. Understand our multiplicative probability model, data sources, and calculation process.',
    keywords: 'statistical methodology, probability calculation, demographic data analysis, multiplicative model, dating statistics',
    canonical: 'https://femalepartnercalculator.com/methodology',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Statistical Methodology for Partner Compatibility Calculation",
      "description": "Detailed explanation of the multiplicative probability model used to calculate partner compatibility based on demographic data",
      "author": {
        "@type": "Organization",
        "name": "Female Partner Calculator"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Female Partner Calculator"
      },
      "dateModified": "2025-08-25"
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Statistical Methodology
            </h1>
            <p className="text-xl text-muted-foreground">
              How we calculate compatibility probabilities using demographic
              data
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
              <CardDescription>
                Our approach to calculating statistical compatibility
                probabilities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="leading-relaxed">
                The Female Partner Calculator uses a{" "}
                <strong>multiplicative probability model </strong>
                based on real demographic data from authoritative government
                sources. This approach calculates the statistical likelihood of
                finding a partner who meets all specified criteria within the
                U.S. adult female population.
              </p>
              <p className="leading-relaxed">
                Our methodology prioritizes statistical accuracy, transparency,
                and reproducibility. All calculations are based on recent
                government surveys and peer-reviewed research, with proper
                attribution and source documentation for every data point used.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Mathematical Model</CardTitle>
              <CardDescription>
                The statistical framework behind our probability calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Basic Formula</h4>
                <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                  P(Compatible) = P(Age) × P(Race) × P(Politics|Race) ×
                  P(Feminist|Race,Politics) × P(NoChildren|Race,Age) ×
                  P(BMI|Race) × P(IntactFamily|Race) × P(MaritalStatus|Race,Age)
                  × P(SexualPartnerCount|Race,Age) × P(Education|Race,Age)
                </div>
                <p className="text-muted-foreground mt-3 text-sm">
                  Where each P(factor) represents the probability that a
                  randomly selected woman meets that specific criterion.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-3">
                  Conditional Probabilities
                </h4>
                <p className="text-muted-foreground text-sm mb-3">
                  Our model accounts for the fact that demographic factors are
                  not independent. For example:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>
                    • Political affiliation varies significantly by race and
                    region
                  </li>
                  <li>
                    • Single motherhood rates differ across racial and age
                    groups
                  </li>
                  <li>
                    • BMI distributions vary by demographic characteristics
                  </li>
                  <li>
                    • Intact family backgrounds correlate with other factors
                  </li>
                  <li>
                    • Sexual behavior patterns correlate with age and
                    demographic factors
                  </li>
                  <li>
                    • Educational attainment correlates with age, race, and
                    other demographic factors
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Age Range Handling</h4>
                <p className="text-muted-foreground text-sm">
                  For age ranges (e.g., 22-35), we calculate the proportional
                  probability based on overlap with age group distributions.
                  Each age group (18-24, 25-34, 35-44) contributes
                  proportionally to the final probability, ensuring that
                  narrower ranges result in lower probabilities.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data Integration Process</CardTitle>
              <CardDescription>
                How we combine multiple data sources for accurate calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Badge variant="outline" className="mb-2">
                    Step 1
                  </Badge>
                  <h4 className="font-semibold mb-2">Source Verification</h4>
                  <p className="text-sm text-muted-foreground">
                    We identify authoritative sources (Census Bureau, CDC, Pew
                    Research) and verify data collection methodologies and
                    sample sizes.
                  </p>
                </div>
                <div>
                  <Badge variant="outline" className="mb-2">
                    Step 2
                  </Badge>
                  <h4 className="font-semibold mb-2">Data Extraction</h4>
                  <p className="text-sm text-muted-foreground">
                    Raw data is extracted from official reports and datasets,
                    focusing on adult female populations aged 18-100.
                  </p>
                </div>
                <div>
                  <Badge variant="outline" className="mb-2">
                    Step 3
                  </Badge>
                  <h4 className="font-semibold mb-2">Cross-Validation</h4>
                  <p className="text-sm text-muted-foreground">
                    Statistics are cross-referenced across multiple sources to
                    ensure consistency and identify any discrepancies.
                  </p>
                </div>
                <div>
                  <Badge variant="outline" className="mb-2">
                    Step 4
                  </Badge>
                  <h4 className="font-semibold mb-2">Integration</h4>
                  <p className="text-sm text-muted-foreground">
                    Data is integrated into our calculation engine with proper
                    weighting for demographic intersections and dependencies.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Assumptions</CardTitle>
              <CardDescription>
                Important assumptions underlying our statistical model
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Representative Sampling
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Government surveys use scientifically rigorous sampling
                    methods that accurately represent the U.S. population.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold mb-1">Response Accuracy</h4>
                  <p className="text-sm text-muted-foreground">
                    Survey respondents provide honest and accurate answers about
                    their demographic characteristics and preferences.
                  </p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold mb-1">Temporal Stability</h4>
                  <p className="text-sm text-muted-foreground">
                    Demographic patterns remain relatively stable over the 1-3
                    year period between data collection and our calculations.
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold mb-1">Geographic Uniformity</h4>
                  <p className="text-sm text-muted-foreground">
                    National statistics provide reasonable approximations for
                    local dating markets, though regional variation exists.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Calculation Examples</CardTitle>
              <CardDescription>
                Step-by-step walkthrough of probability calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Example Scenario</h4>
                <div className="bg-muted rounded-lg p-4 text-sm">
                  <p className="mb-2">
                    <strong>Preferences:</strong>
                  </p>
                  <ul className="space-y-1 ml-4">
                    <li>• Age: 22-35 years old</li>
                    <li>• Race: White (Non-Hispanic)</li>
                    <li>• Politics: Conservative</li>
                    <li>• Feminist: No</li>
                    <li>• Children: No children</li>
                    <li>• Body Type: Not overweight (BMI &lt; 25)</li>
                    <li>• Family: Raised by two parents</li>
                    <li>• Marital Status: Never married</li>
                    <li>• Sexual Experience: Virgin/Pure (0 partners)</li>
                    <li>• Education: Bachelor's degree or higher</li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Step-by-Step Calculation</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b pb-1">
                    <span>Age 22-35:</span>
                    <span className="font-mono">0.28 (28%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>White race:</span>
                    <span className="font-mono">0.61 (61%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>Conservative (given White):</span>
                    <span className="font-mono">0.35 (35%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>Not feminist (given White, Conservative):</span>
                    <span className="font-mono">0.78 (78%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>No children (given White, age 22-35):</span>
                    <span className="font-mono">0.42 (42%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>BMI &lt; 25 (given White):</span>
                    <span className="font-mono">0.39 (39%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>Two-parent family (given White):</span>
                    <span className="font-mono">0.77 (77%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>Never married (given White, age 22-35):</span>
                    <span className="font-mono">0.45 (45%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>Virgin/Pure (given White, age 22-35):</span>
                    <span className="font-mono">0.03 (3%)</span>
                  </div>
                  <div className="flex justify-between border-b pb-1">
                    <span>Bachelor's+ (given White, age 22-35):</span>
                    <span className="font-mono">0.45 (45%)</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t-2">
                    <span>Combined Probability:</span>
                    <span className="font-mono">0.000031 (0.003%)</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Result Interpretation</h4>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>0.003%</strong> probability means approximately{" "}
                    <strong>3 out of every 100,000</strong> adult women in the
                    United States meet all specified criteria. With ~168 million
                    adult women nationally, this represents approximately{" "}
                    <strong>5,040 compatible individuals</strong>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Limitations & Disclaimers</CardTitle>
              <CardDescription>
                Important considerations when interpreting results
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-yellow-800 dark:text-yellow-200">
                  Statistical vs. Personal Reality
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  These calculations represent population-level probabilities,
                  not personal dating outcomes. Individual factors like social
                  circles, geographic location, personal attractiveness, and
                  social skills significantly affect real-world compatibility.
                </p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-red-800 dark:text-red-200">
                  Not Relationship Advice
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300">
                  This tool provides statistical analysis only and should not be
                  used as the basis for relationship decisions, dating
                  strategies, or personal life choices. Professional counseling
                  is recommended for relationship guidance.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-200">
                  Data Limitations
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  All statistics are subject to sampling error, survey response
                  bias, temporal lag, and the inherent limitations of
                  demographic categorization. Individual preferences and
                  compatibility extend far beyond measurable demographics.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <p className="text-muted-foreground">
              For more details, see our{" "}
              <a href="/data-sources" className="text-primary hover:underline">
                data sources
              </a>{" "}
              or{" "}
              <a href="/faq" className="text-primary hover:underline">
                frequently asked questions
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Methodology;
